/**
 * DepositDetailPage - Página de detalle de un depósito
 */
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Button,
  Paper,
  Grid,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  Chip,
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useDeposito, useNotification } from '../context';
import PageHeader from '../components/common/PageHeader';
import LoadingSpinner from '../components/common/LoadingSpinner';

const DepositDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentDeposit, getDepositoById, downloadDepositPDF, loading } = useDeposito();
  const { showSuccess, showError } = useNotification();

  // Cargar depósito al montar
  useEffect(() => {
    if (id) {
      loadDeposit();
    }
  }, [id]);

  const loadDeposit = async () => {
    try {
      await getDepositoById(id);
    } catch (error) {
      showError('Error al cargar depósito');
      navigate('/depositos');
    }
  };

  const handleDownloadPDF = async () => {
    try {
      await downloadDepositPDF(
        currentDeposit.id,
        `deposito-${currentDeposit.numero_boleta || currentDeposit.id}.pdf`
      );
      showSuccess('PDF descargado exitosamente');
    } catch (error) {
      showError('Error al descargar PDF');
    }
  };

  const handleBack = () => {
    navigate('/depositos');
  };

  // Formatear moneda
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
    }).format(value);
  };

  // Formatear fecha
  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('es-AR');
  };

  // Formatear fecha y hora
  const formatDateTime = (date) => {
    return new Date(date).toLocaleString('es-AR');
  };

  if (loading || !currentDeposit) {
    return <LoadingSpinner message="Cargando depósito..." />;
  }

  // Calcular total de cheques
  const totalCheques = currentDeposit.cheques?.reduce(
    (sum, cheque) => {
      const monto = parseFloat(cheque.monto);
      return sum + (isNaN(monto) ? 0 : monto);
    },
    0
  ) || 0;

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <PageHeader
        title={`Depósito ${currentDeposit.numero_boleta || `#${currentDeposit.id}`}`}
        subtitle="Información detallada del depósito"
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Depósitos', path: '/depositos' },
          { label: 'Detalle' },
        ]}
        actions={
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={handleBack}
            >
              Volver
            </Button>
            <Button
              variant="contained"
              startIcon={<PictureAsPdfIcon />}
              onClick={handleDownloadPDF}
            >
              Descargar PDF
            </Button>
          </Box>
        }
      />

      <Grid container spacing={3}>
        {/* Información del depósito */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Información del Depósito
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Fecha de Depósito
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(currentDeposit.fecha_deposito)}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Estado
                  </Typography>
                  <Chip
                    label={currentDeposit.estado || 'Completado'}
                    color="success"
                    size="small"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Cuenta Bancaria Destino
                  </Typography>
                  <Typography variant="body1">
                    {currentDeposit.cuenta_bancaria?.titular || '-'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {currentDeposit.cuenta_bancaria?.numero_cuenta || ''}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Banco
                  </Typography>
                  <Typography variant="body1">
                    {currentDeposit.cuenta_bancaria?.banco?.nombre || currentDeposit.banco_destino?.nombre || '-'}
                  </Typography>
                </Grid>

                {currentDeposit.numero_boleta && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Número de Comprobante
                    </Typography>
                    <Typography variant="body1">
                      {currentDeposit.numero_boleta}
                    </Typography>
                  </Grid>
                )}

                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Usuario que realizó el depósito
                  </Typography>
                  <Typography variant="body1">
                    {currentDeposit.usuario?.nombre || '-'} {currentDeposit.usuario?.apellido || ''}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {currentDeposit.usuario?.email || ''}
                  </Typography>
                </Grid>

                {currentDeposit.observaciones && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Observaciones
                    </Typography>
                    <Typography variant="body1">
                      {currentDeposit.observaciones}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Resumen */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Resumen
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Cantidad de Cheques
                </Typography>
                <Typography variant="h4" color="primary">
                  {currentDeposit.cheques?.length || 0}
                </Typography>
              </Box>

              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Monto Total
                </Typography>
                <Typography variant="h4" color="primary">
                  {formatCurrency(totalCheques)}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Tabla de cheques incluidos */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Cheques Incluidos
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Número Cheque</TableCell>
                      <TableCell>Cliente</TableCell>
                      <TableCell>Banco Origen</TableCell>
                      <TableCell>Fecha Pago</TableCell>
                      <TableCell align="right">Monto</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {currentDeposit.cheques && currentDeposit.cheques.length > 0 ? (
                      <>
                        {currentDeposit.cheques.map((cheque, index) => (
                          <TableRow key={cheque.id || index} hover>
                            <TableCell>{cheque.numero_cheque || '-'}</TableCell>
                            <TableCell>{cheque.cliente?.nombre || '-'}</TableCell>
                            <TableCell>{cheque.banco?.nombre || '-'}</TableCell>
                            <TableCell>
                              {cheque.fecha_pago ? formatDate(cheque.fecha_pago) : '-'}
                            </TableCell>
                            <TableCell align="right">
                              {formatCurrency(parseFloat(cheque.monto) || 0)}
                            </TableCell>
                          </TableRow>
                        ))}
                        {/* Fila de total */}
                        <TableRow>
                          <TableCell colSpan={4} align="right">
                            <Typography variant="h6">Total:</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="h6" color="primary">
                              {formatCurrency(totalCheques)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      </>
                    ) : (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                          No hay cheques incluidos en este depósito
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default DepositDetailPage;
